import React from 'react';

import * as Yup from 'yup';
import { Box, ButtonV2, DialogV2, Flex, FormControl, InputV2, TextArea } from '@withjoy/joykit';

import { useFormik } from 'formik';
import { ReactComponent as IconBrandLogoDesktop } from '@assets/icons/icon-brand-logo-desktop.svg';
import { useTranslation } from '@shared/core';
import { OpenInNewWindow } from '@withjoy/joykit/icons';

import { formatPriceInputValue } from './ExternalProductPdpModal.utils';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { ExternalProduct, ExternalProductOffer } from '../../../../Catalog.types';

type ExternalProductPdpModalProps = {
  isOpen: boolean;
  onClose: () => void;
  offer: ExternalProductOffer;
  product?: ExternalProduct;
  isProductLoading: boolean;
  onSubmitForm: (args: ExternalProductPdpModalForm) => void;
};

export type ExternalProductPdpModalForm = {
  title: string;
  price: string;
  quantity: number;
  note: string;
};

const ExternalProductPdpModalBody = (props: ExternalProductPdpModalProps) => {
  const { offer, onClose, onSubmitForm, product, isProductLoading } = props;
  const { t, tq } = useTranslation('catalogRegistry');

  const pdpModalTrans = t('universalSearch', 'pdpModal');

  const formik = useFormik<ExternalProductPdpModalForm>({
    initialValues: {
      title: offer.productTitle,
      price: `${offer.sellerSalePrice?.floatingPointDecimalString ?? offer.sellerFullPrice.floatingPointDecimalString}`,
      quantity: 1,
      note: '',
    },
    validationSchema: Yup.object<ExternalProductPdpModalForm>({
      title: Yup.string().required(),
      price: Yup.string().required(),
      quantity: Yup.number().required(),
      note: Yup.string(),
    }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: values => {
      onSubmitForm(values);
    },
  });

  const handleOnQuantityInputBlur = useEventCallback<React.FocusEventHandler<HTMLInputElement>>(e => {
    const value = e.currentTarget.value;
    const parsedValue = parseFloat(value);
    if (Number.isNaN(parsedValue) || parsedValue < 1) {
      formik.setFieldValue('quantity', 1);
    }
  });

  const productUrl = offer.sellerUrl ?? product?.sellers.find(seller => seller.name === offer.sellerName)?.url;

  return (
    <>
      <DialogV2.Body>
        <Flex
          flexDirection={['column', null, 'row']}
          columnGap={[null, null, 7]}
          rowGap={[7, null, 0]}
        >
          {/* Product photo */}
          <Box
            width={['100%', null, null, '40%']}
            maxWidth={[null, null, null, '40%']}
          >
            <Flex
              flexDirection="column"
              rowGap={5}
            >
              <Box
                __css={{ width: '100%', paddingTop: '100%', backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
                backgroundImage={`url('${offer.productThumbnail.url}')`}
              />
              {productUrl && (
                <ButtonV2
                  as="a"
                  target="_blank"
                  href={productUrl}
                  variant="link"
                  intent="neutral"
                  typographyVariant={'button1'}
                  endIcon={<OpenInNewWindow />}
                >
                  <Box
                    as="span"
                    textOverflow="ellipsis"
                    overflow="hidden"
                  >
                    {pdpModalTrans.viewShopOnLink({ shopName: offer.sellerName })}
                  </Box>
                </ButtonV2>
              )}
            </Flex>
          </Box>

          {/* Details */}
          <Flex
            flexDirection="column"
            rowGap={7}
          >
            <FormControl
              label={pdpModalTrans.formFields.title.label()}
              isInvalid={!!formik.errors.title}
            >
              <InputV2 {...formik.getFieldProps('title')} />
            </FormControl>
            <Flex columnGap={6}>
              <FormControl
                label={tq`Price`}
                isInvalid={!!formik.errors.price}
              >
                <InputV2
                  type="text"
                  pattern="[0-9]*(.[0-9]+)?"
                  inputMode="decimal"
                  {...formik.getFieldProps('price')}
                  value={formatPriceInputValue(formik.values.price)}
                  isReadOnly={true}
                />
              </FormControl>
              <FormControl
                label={pdpModalTrans.formFields.quantity.label()}
                isInvalid={!!formik.errors.quantity}
              >
                <InputV2
                  type="number"
                  min={1}
                  {...formik.getFieldProps('quantity')}
                  onBlur={handleOnQuantityInputBlur}
                />
              </FormControl>
            </Flex>

            <FormControl label={pdpModalTrans.formFields.note.label()}>
              <TextArea
                placeholder={pdpModalTrans.formFields.note.placeholder()}
                {...formik.getFieldProps('note')}
                resize="vertical"
              />
            </FormControl>
          </Flex>
        </Flex>
      </DialogV2.Body>
      <DialogV2.Footer justifyContent={'space-between'}>
        <ButtonV2
          variant="ghost"
          intent="neutral"
          onClick={onClose}
        >
          {pdpModalTrans.cancelCta()}
        </ButtonV2>
        <ButtonV2
          intent="neutral"
          disabled={!formik.isValid}
          loading={isProductLoading}
          onClick={() => formik.submitForm()}
        >
          {pdpModalTrans.addCta()}
        </ButtonV2>
      </DialogV2.Footer>
    </>
  );
};

export const ExternalProductPdpModal = (props: ExternalProductPdpModalProps) => {
  const { isOpen, onClose, offer } = props;

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
      enableDividers
      size="2xl"
      stickyBehavior="header-footer"
      overrides={{
        CloseButton: {
          props: {
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'mono5',
            height: 8,
            width: 8,
            top: 6,
            right: 7,
            color: 'mono14',
            minWidth: 'unset',
            padding: 0,
            borderRadius: 'full',
          },
        },
      }}
    >
      {offer && (
        <>
          <DialogV2.Header>
            <IconBrandLogoDesktop />
            <DialogV2.CloseButton />
          </DialogV2.Header>
          <ExternalProductPdpModalBody {...props} />
        </>
      )}
    </DialogV2>
  );
};
